import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Linking,
  Platform,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import versionConfig from '../../config/version';
import updateCheckerService from '../../services/updateCheckerService';

// Import icons
import backIcon from '../../assets/icons/back.png';
import chevronIcon from '../../assets/icons/chevron.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('About');

interface AboutScreenProps {
  navigation: NavigationProp<any>;
}

const AboutScreen: React.FC<AboutScreenProps> = ({ navigation }) => {
  const [versionInfo, setVersionInfo] = useState<string>('');
  const [fullVersion, setFullVersion] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);

  useEffect(() => {
    loadVersionInfo();
  }, []);

  const loadVersionInfo = async () => {
    setIsLoading(true);
    try {
      const version = await versionConfig.getVersionString();
      const fullVersionStr = await versionConfig.getFullVersionString();
      setVersionInfo(version);
      setFullVersion(fullVersionStr);
    } catch (error) {
      logger.error('Failed to load version info:', error);
      setVersionInfo('Unknown');
      setFullVersion('Unknown');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckForUpdates = async () => {
    setIsCheckingUpdate(true);
    try {
      await updateCheckerService.forceCheckForUpdates();
    } catch (error) {
      logger.error('Failed to check for updates:', error);
    } finally {
      setIsCheckingUpdate(false);
    }
  };

  const handleViewChangelog = () => {
    navigation.navigate('Changelog');
  };

  const handleOpenWebsite = async () => {
    const url = 'https://www.myfinalkey.com';
    try {
      await Linking.openURL(url);
    } catch (error) {
      logger.error('Failed to open website:', error);
    }
  };

  const handleOpenSupport = async () => {
    const email = 'support@myfinalkey.com';
    const subject = 'MyFinalKey Support Request';
    const url = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
    try {
      await Linking.openURL(url);
    } catch (error) {
      logger.error('Failed to open email:', error);
    }
  };

  const handleCallPhone = async () => {
    try {
      await Linking.openURL('tel:4697342767');
    } catch (error) {
      logger.error('Failed to open phone dialer:', error);
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Image source={backIcon} style={styles.headerIcon} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>About</Text>
          <View style={styles.placeholder} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>About</Text>
        <View style={styles.placeholder} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* App Logo and Name */}
        <View style={styles.logoSection}>
          <Image
            source={require('../../assets/logo.png')}
            style={styles.logo}
          />
          <Text style={styles.appName}>MyFinalKey</Text>
          <Text style={styles.tagline}>
            Secure Your Legacy, Protect Your Loved Ones
          </Text>
        </View>

        {/* Version Info Card */}
        <View style={styles.versionCard}>
          <Text style={styles.versionTitle}>Version Information</Text>
          <Text style={styles.versionText}>{versionInfo}</Text>
          {__DEV__ && (
            <View style={styles.devBadge}>
              <Text style={styles.devBadgeText}>Development Build</Text>
            </View>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.menuSection}>
          <TouchableOpacity
            style={styles.menuItem}
            onPress={handleCheckForUpdates}
            disabled={isCheckingUpdate}
          >
            <Text style={styles.menuItemText}>
              {isCheckingUpdate ? 'Checking for Updates...' : 'Check for Updates'}
            </Text>
            {!isCheckingUpdate && (
              <Image source={chevronIcon} style={styles.chevronIcon} />
            )}
            {isCheckingUpdate && (
              <ActivityIndicator size="small" color="#29BFC0" />
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.menuItem}
            onPress={handleViewChangelog}
          >
            <Text style={styles.menuItemText}>What's New</Text>
            <Image source={chevronIcon} style={styles.chevronIcon} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.menuItem} onPress={handleOpenWebsite}>
            <Text style={styles.menuItemText}>Visit Our Website</Text>
            <Image source={chevronIcon} style={styles.chevronIcon} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.menuItem} onPress={handleOpenSupport}>
            <Text style={styles.menuItemText}>Contact Support</Text>
            <Image source={chevronIcon} style={styles.chevronIcon} />
          </TouchableOpacity>
        </View>

        {/* App Description */}
        <View style={styles.descriptionCard}>
          <Text style={styles.descriptionTitle}>About MyFinalKey</Text>
          <Text style={styles.descriptionText}>
            MyFinalKey is a comprehensive digital vault designed to help you
            organize and secure your personal information, financial details,
            healthcare directives, and final wishes. Our mission is to ensure
            your loved ones have access to critical information when they need
            it most.
          </Text>
        </View>

        {/* Full Version Details (Dev Only) */}
        {__DEV__ && (
          <View style={styles.debugCard}>
            <Text style={styles.debugTitle}>Debug Information</Text>
            <Text style={styles.debugText}>{fullVersion}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            © 2025 MyFinalKey. All rights reserved.
          </Text>
          <Text style={styles.footerText}>
            Southlake, TX 76092 •{' '}
            <Text style={styles.footerLink} onPress={handleCallPhone}>
              469-734-2767
            </Text>
          </Text>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  placeholder: {
    width: s(24),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoSection: {
    alignItems: 'center',
    paddingVertical: ms(30),
  },
  logo: {
    width: s(100),
    height: s(100),
    marginBottom: ms(15),
  },
  appName: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(8),
  },
  tagline: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    paddingHorizontal: ms(20),
  },
  versionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    alignItems: 'center',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  versionTitle: {
    fontSize: ms(14, 0.3),
    color: '#666',
    marginBottom: ms(8),
  },
  versionText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  devBadge: {
    backgroundColor: '#FFF3CD',
    borderRadius: ms(5),
    paddingVertical: ms(5),
    paddingHorizontal: ms(12),
    marginTop: ms(10),
  },
  devBadgeText: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#856404',
  },
  menuSection: {
    marginBottom: ms(20),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    padding: ms(15),
    borderRadius: ms(12),
    marginBottom: ms(12),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  menuItemText: {
    fontSize: ms(16, 0.3),
    color: '#333',
    fontWeight: '500',
  },
  chevronIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#666',
  },
  descriptionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  descriptionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(12),
  },
  descriptionText: {
    fontSize: ms(15, 0.3),
    color: '#666',
    lineHeight: ms(24, 0.3),
  },
  debugCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(15),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#dee2e6',
  },
  debugTitle: {
    fontSize: ms(14, 0.3),
    fontWeight: 'bold',
    color: '#666',
    marginBottom: ms(10),
  },
  debugText: {
    fontSize: ms(12, 0.3),
    color: '#333',
    fontFamily: 'monospace',
    lineHeight: ms(20, 0.3),
  },
  footer: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  footerText: {
    fontSize: ms(13, 0.3),
    color: '#999',
    marginBottom: ms(5),
  },
  footerLink: {
    color: '#29BFC0',
    textDecorationLine: 'underline',
  },
  bottomPadding: {
    height: s(20),
  },
});

export default AboutScreen;
